import { Printer } from 'lucide-react'
import { type ReactNode } from 'react'

import type { SalesDocumentDetail } from '@aeris/contracts'
import { MONEY_SCALE, parseHumanAmount, ZERO_MONEY } from '@aeris/money'
import {
  quoteFor,
  renderReceiptDocumentHtml,
  renderText,
  type PaperWidth,
} from '@aeris/printing'
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  Field,
  Select,
} from '@aeris/ui'

import { printHtmlDocument } from '../lib/label-print.ts'
import { printedCustomerOf } from '../lib/printed-customer.ts'
import {
  useCompanyHeader,
  useReceiptTemplate,
} from '../lib/use-receipt-template.ts'

/**
 * O orçamento na tela e no papel (F034).
 *
 * Irmão do `ReceiptDialog` e pelo mesmo desenho: o documento é DADO, produzido
 * por `@aeris/printing`, e isto é um renderizador dele — o do navegador. O
 * mesmo `PrintDocument` já codifica para ESC/POS, que é o que o Edge e o
 * desktop vão mandar direto para a bobina quando o transporte existir. O papel
 * não muda; o transporte sim.
 *
 * A diferença que importa entre este e o recibo não é visual: **o recibo conta
 * o que aconteceu, o orçamento promete o que vai acontecer**. O cliente sai com
 * ele, atravessa a loja e o entrega no caixa — então o número precisa ser lido
 * em voz alta e bipado, e o papel precisa dizer que não é comprovante de
 * pagamento.
 */

const money = (wire: string) =>
  parseHumanAmount(wire.replace('.', ','), MONEY_SCALE) ?? ZERO_MONEY

/** `2,5` e não `2.500000`, que é o que uma pessoa lê no papel. */
const quantity = (wire: string): string =>
  wire.replace(/\.?0+$/, '').replace('.', ',')

export type QuoteDialogProps = {
  quote: SalesDocumentDetail
  paper: PaperWidth
  onPaperChange: (paper: PaperWidth) => void
  onClose: () => void
}

export const QuoteDialog = ({
  quote,
  paper,
  onPaperChange,
  onClose,
}: QuoteDialogProps): ReactNode => {
  // O cabeçalho da loja e o modelo dela (F149), das mesmas consultas que o PDV
  // já tem em cache quando este diálogo abre.
  const company = useCompanyHeader()
  const template = useReceiptTemplate()

  const document = quoteFor({
    template,
    company,
    branchName: quote.branchName,
    /*
     * Zero nunca chega aqui: o orçamento só é impresso depois de sair de
     * `draft`, e é essa transição que aloca o número. O fallback existe porque
     * o contrato admite nulo para os rascunhos, não porque este caso acontece.
     */
    number: quote.number ?? 0,
    issuedAt: new Date(quote.issuedAt),
    validUntil: quote.validUntil === null ? null : new Date(quote.validUntil),
    customerName: quote.customerName,
    customer: printedCustomerOf(quote),
    salespersonName: quote.salespersonName,
    lines: quote.lines.map((line) => ({
      description: line.description,
      quantity: quantity(line.quantity),
      unit: line.unit,
      unitPrice: money(line.unitPrice),
      lineTotal: money(line.lineTotal),
      code: line.sku,
      location: line.location,
      discount: money(line.discountAmount),
    })),
    subtotal: money(quote.subtotalAmount),
    discount: money(quote.discountAmount),
    total: money(quote.totalAmount),
    reservesStock: quote.reservesStock,
    documentId: quote.id,
  })

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent
        title={document.title}
        description="Entregue este papel ao cliente. Ele o leva ao caixa para retirar."
      >
        <div className="flex flex-col gap-3">
          <Field label="Papel" className="w-44">
            {(field) => (
              <Select
                {...field}
                value={String(paper)}
                onChange={(event) => {
                  onPaperChange(event.target.value === '48' ? 48 : 32)
                }}
              >
                <option value="32">58 mm</option>
                <option value="48">80 mm</option>
              </Select>
            )}
          </Field>

          {/*
            O mesmo `id` que o recibo usa, porque a folha de estilo de impressão
            guarda exatamente este elemento e esconde o resto da página. Sem
            isso, imprimir o orçamento imprime a barra lateral e o carrinho
            atrás dele.
          */}
          <pre
            id="aeris-receipt"
            aria-label="Orçamento"
            className="overflow-x-auto rounded-(--radius-md) border border-(--border-subtle) bg-(--surface) p-3 font-(--font-mono) text-(length:--text-xs) leading-tight text-(--text-primary)"
          >
            {renderText(document, paper)}
          </pre>
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Fechar
          </Button>
          <Button
            variant="primary"
            leadingIcon={<Printer aria-hidden="true" className="h-4 w-4" />}
            onClick={() => {
              /*
               * Documento próprio, na largura da bobina e na altura do texto
               * (`renderReceiptDocumentHtml`, F140: negrito, TOTAL grande e o
               * código de barras de verdade). Se o quadro não abrir, cai no
               * jeito antigo, que imprime a página com o resto escondido.
               */
              printHtmlDocument(
                renderReceiptDocumentHtml(document, paper)
              ).catch(() => {
                window.print()
              })
            }}
          >
            Imprimir
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
